import * as Sentry from "@sentry/vue";
import type { AxiosResponse } from "axios";
import { defineStore } from "pinia";
import { ref } from "vue";
import { PAYMENT_METHOD_CONFIG } from "../../../../../../legl-ui/payment-wrapper/config/payment-config";
import { RenderPayment } from "../../../../../../legl-ui/payment-wrapper/services/render-payment";
import {
  http,
  showErrorToast,
} from "../../../../../request-manager/axios-http";
import type { CompanyDetails, Step } from "../../../../types";
import { getSteps } from "../composables/getSteps";
import { PaymentSources } from "../constants/payment-sources";
import { StepValues } from "../constants/step-values";
import type {
  ClosedPaymentDetails,
  CompanyPaymentSettings,
  PaymentDetails,
  PaymentMethod,
  PaymentSource,
  PaymentSteps,
  Session,
} from "../types";

export const usePayStore = defineStore("pay-store", () => {
  window.addEventListener("resize", () => {
    isDesktop.value = window.innerWidth > 800;
  });

  const isDesktop = ref(window.innerWidth > 800);

  const isInitialised = ref(false);
  const payRequestError = ref(false);
  const payRequestComplete = ref(false);
  const payRequestClosed = ref(false);
  const companyDetails = ref<CompanyDetails | null>(null);
  const selectedPaymentProvider = ref<string | null>(null);
  const paymentDetails = ref<PaymentDetails | null>(null);
  const companyPaymentSettings = ref<CompanyPaymentSettings | null>(null);

  const companySlug = getCompanySlugFromUrlHost();
  const pathParameter = getFourthPathParemeter();
  const paymentSource = ref<PaymentSource>(PaymentSources.CHECKOUT);

  const steps = ref<PaymentSteps>([]);
  const currentStep = ref<Step<any> | null>(null);
  const stepSubmitting = ref(false);

  const availablePaymentMethods = ref<PaymentMethod[]>([]);
  const selectedPaymentMethod = ref<PaymentMethod | null>();
  const providerDetails = ref();
  const loadingProviderDetails = ref(false);

  async function initialiseApp() {
    try {
      if (pathParameter) {
        await getPaymentSource();
      }

      const promises = [getCompanyDetails(), getCompanyPaymentSettings()];

      if (
        paymentSource.value === PaymentSources.PAY_REQUEST ||
        paymentSource.value === PaymentSources.PAYMENT_PLAN
      ) {
        promises.push(getPaymentDetails());
      }

      await Promise.all(promises);

      steps.value = getSteps(paymentSource.value, paymentDetails.value);

      setCurrentStep(steps.value[0]);

      isInitialised.value = true;

      Sentry.addBreadcrumb({
        category: "pay",
        message: "App initialised",
        level: "info",
      });
    } catch (error) {
      payRequestError.value = true;
      console.error(error);
      Sentry.addBreadcrumb({
        category: "pay",
        message: "App failed to initialise",
        level: "error",
      });
    }
  }

  async function getPaymentSource() {
    const url = `/api/payment_source/${pathParameter}/`;

    const { data } = await http.get<{ payment_source: PaymentSource }>(url);

    paymentSource.value = data.payment_source;
  }

  async function getCompanyDetails() {
    const url = `/api/companies/${companySlug}/`;

    const { data } = await http.get<CompanyDetails>(url);

    companyDetails.value = data;
  }

  async function getCompanyPaymentSettings() {
    let url = "/api/company_payment_settings/";

    if (pathParameter) {
      url = `/api/company_payment_settings/${pathParameter}/`;
    }
    const { data } = await http.get<CompanyPaymentSettings>(url);
    companyPaymentSettings.value = data;
  }

  async function getPaymentDetails() {
    const url = `/api/payments/${pathParameter}/`;

    const { data } = await http.get<PaymentDetails | ClosedPaymentDetails>(url);
    let has_archived_bank_account = false;
    if ("archived_bank_account" in data) {
      has_archived_bank_account = data.archived_bank_account;
    }
    payRequestClosed.value = has_archived_bank_account || data.is_closed;
    if ("session_url" in data && !payRequestClosed.value) {
      setPaymentDetails(data);
      setAvailablePaymentMethods(data.providers);
    }
  }

  function setPaymentDetails(details: PaymentDetails) {
    paymentDetails.value = details;
  }

  async function acceptTerms() {
    const url = `/api/payments/${pathParameter}/terms/`;
    await http.put(url);
  }

  function setCurrentStep(step: Step<any>) {
    // Reset payment method and provider details when moving back from the payment step to the transaction details step
    if (
      step.value === StepValues.TRANSACTION_DETAILS &&
      currentStep.value?.value === StepValues.PAYMENT
    ) {
      selectedPaymentMethod.value = null;
      providerDetails.value = null;
    }
    currentStep.value = step;
  }

  function getCompanySlugFromUrlHost() {
    const url = new URL(window.location.href);
    return url.host.split(".")[0];
  }

  function getFourthPathParemeter() {
    const url = new URL(window.location.href);
    return url.pathname.split("/")[3];
  }

  async function setCurrentStepComplete() {
    if (!currentStep.value) {
      return;
    }

    switch (currentStep.value.value) {
      case "get-started":
        try {
          stepSubmitting.value = true;
          await acceptTerms();
          Sentry.addBreadcrumb({
            category: "pay",
            message: "Terms and conditions accepted",
            level: "info",
          });
          setCompletedAt(currentStep.value);
          stepForward();
        } catch (error) {
          showErrorToast(error);
        } finally {
          stepSubmitting.value = false;
        }
        break;
      case "transaction-details":
      case "payment-plan-overview":
        setCompletedAt(currentStep.value);
        stepForward();
        break;
      case "payment":
        setCompletedAt(currentStep.value);
        payRequestComplete.value = true;
        Sentry.addBreadcrumb({
          category: "pay",
          message: "Payment complete",
          level: "info",
        });
        break;
      default:
        break;
    }
  }

  function setCompletedAt(step: Step<any>) {
    if (!step) {
      return;
    }
    step.completed_at = new Date().toISOString();
  }

  function stepBack() {
    const currentStepIndex = steps.value.findIndex(
      (step) => step.value === currentStep.value?.value,
    );

    const previousStep = steps.value?.[currentStepIndex - 1];

    if (previousStep) {
      setCurrentStep(previousStep);
    }
  }

  function stepForward() {
    const currentStepIndex = steps.value.findIndex(
      (step) => step.value === currentStep.value?.value,
    );

    if (currentStepIndex === steps.value.length - 1) {
      return;
    }

    setCurrentStep(steps.value[currentStepIndex + 1]);
  }

  function setAvailablePaymentMethods(providers: string[]) {
    availablePaymentMethods.value = PAYMENT_METHOD_CONFIG.filter(
      (payment_config) => {
        const availablePaymentMethod = providers?.includes(
          payment_config.external_provider_key,
        );
        return availablePaymentMethod;
      },
    );
  }

  function selectPaymentMethod(method: PaymentMethod) {
    selectedPaymentMethod.value = method;
  }

  async function getProviderDetails() {
    loadingProviderDetails.value = true;
    try {
      if (selectedPaymentMethod.value && paymentDetails.value) {
        const requestBody = {
          provider: selectedPaymentMethod.value.provider,
          method: selectedPaymentMethod.value.method,
        };
        if (paymentDetails.value.session_url) {
          const { data } = await http.put<
            { method: string; provider: string },
            AxiosResponse<Session>
          >(paymentDetails.value.session_url, requestBody);
          providerDetails.value = data.provider_details;
          providerDetails.value.paymentUrl = paymentDetails.value.url;
        }
      } else {
        throw new Error("Payment method or payment details not set");
      }
    } catch (error) {
      console.error(error);
    } finally {
      loadingProviderDetails.value = false;
    }
  }

  async function handleMethodSelection(paymentMethod: PaymentMethod) {
    selectPaymentMethod(paymentMethod);
    Sentry.addBreadcrumb({
      category: "pay",
      message: "Payment method selected",
      data: paymentMethod,
      level: "info",
    });
    await getProviderDetails();
    await renderPayment();
  }

  async function renderPayment() {
    try {
      await RenderPayment.render(
        selectedPaymentMethod.value?.provider,
        paymentDetails.value?.amount,
        providerDetails.value,
        paymentDetails.value,
        companyDetails.value,
      );
    } catch (error) {
      console.error(error);
    }
  }

  initialiseApp();

  return {
    isDesktop,
    isInitialised,
    payRequestError,
    companyDetails,
    companyPaymentSettings,
    selectedPaymentProvider,
    paymentDetails,
    setPaymentDetails,
    setCurrentStep,
    setCurrentStepComplete,
    stepBack,
    currentStep,
    stepSubmitting,
    steps,
    payRequestComplete,
    payRequestClosed,
    checkoutSlug: pathParameter,
    paymentSource,
    setAvailablePaymentMethods,
    availablePaymentMethods,
    selectedPaymentMethod,
    loadingProviderDetails,
    providerDetails,
    handleMethodSelection,
  };
});
